import React, { useEffect, useRef, useState } from "react";
import { Layout, Drawer, Grid } from "antd";
import { useNavigate } from "react-router-dom";
import { DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import "./Sidebar.scss";
import {
  ChartArea,
  ChartColumnStacked,
  ChartNetwork,
  ChartNoAxesCombined,
  CircleHelp,
  Layers,
  Menu,
  SquareChartGantt,
} from "lucide-react";
import { fetchAllDashboardsAPI } from "src/services/AccountDashboardAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  DASHBOARD_APP_BASE,
  DASHBOARD_ROUTE_CONFIG,
  buildUserAccessibleDashboardsFromApi,
} from "./DashboardRoutes";
import {
  hasUserManagementAccess,
} from "src/utils/sectionAccess";
import { useLanguage } from "src/hooks/useLanguage";

const { Sider } = Layout;
type SidebarMode = "collapsed" | "open";

const HELP_SUPPORT_ROUTE = "/account/help-and-support";

interface SidebarItem {
  name: string;
  route?: string;
  children?: SidebarItem[];
}

interface SidebarSection {
  key: string;
  label: string;
  icon: React.ReactNode;
  route?: string;
  items?: SidebarItem[];
}

const filterSidebarSections = (sections: SidebarSection[]) => {
  let next = sections;
  if (!hasUserManagementAccess()) {
    next = next.filter(section => section.key !== "userManagement");
  }
  return next;
};

const dashboardIconMap: Record<string, React.ReactNode> = {
  "Geo-Spatial Analytics Dashboard": (
    <img src="/Icons/salesIcon.svg" alt="Sales" className="sidebar-icon-img" />
  ),
  "Product Analytics Dashboard": (
    <ChartNoAxesCombined className="sidebar-icon-img" />
  ),
  "Web Analytics Dashboard": <ChartNetwork className="sidebar-icon-img" />,
  "Meta Ads Overview": (
    <img src="/Icons/metaIcon.svg" alt="Meta" className="sidebar-icon-img" />
  ),
  "LTV Analysis Dashboard": (
    <img src="/Icons/LTV.svg" alt="LTV" className="sidebar-icon-img" />
  ),
  "Customer Attribution Dashboard": (
    <ChartArea className="sidebar-icon-img" />
  ),
  "Sales & Product Analytics Dashboard": (
    <SquareChartGantt
      className="sidebar-icon-img"
      style={{ color: "#ffffff", stroke: "#ffffff" }}
    />
  ),
  "Omnichannel Analytics Dashboard": (
    <Layers
      className="sidebar-icon-img"
      style={{ color: "#ffffff", stroke: "#ffffff" }}
    />
  ),
  "CLTV Dashboard": <ChartColumnStacked className="sidebar-icon-img" />,
  "Organic Media Dashboard": (
    <img
      src="/Icons/organicmap-grey.svg"
      alt="CLTV"
      className="sidebar-icon-img"
    />
  ),
  "Paid Media Dashboard": (
    <img src="/Icons/paid.svg" alt="Paid" className="sidebar-icon-img" />
  ),
  "Marketing Analytics Dashboard": (
    <img
      src="/Icons/analytics.svg"
      alt="Marketing"
      className="sidebar-icon-img"
    />
  ),
};

const staticRouteIconMap: Record<string, React.ReactNode> = {
  [DASHBOARD_ROUTE_CONFIG[280].route]: (
    <img src="/Icons/salesIcon.svg" alt="Sales" className="sidebar-icon-img" />
  ),
  [DASHBOARD_ROUTE_CONFIG[293].route]: (
    <ChartNetwork className="sidebar-icon-img" />
  ),
  [DASHBOARD_ROUTE_CONFIG[301].route]: (
    <img
      src="/Icons/organicmap-grey.svg"
      alt="Organic"
      className="sidebar-icon-img"
    />
  ),
  [DASHBOARD_ROUTE_CONFIG[296].route]: (
    <ChartColumnStacked className="sidebar-icon-img" />
  ),
  [DASHBOARD_ROUTE_CONFIG[302].route]: (
    <img src="/Icons/paid.svg" alt="Paid" className="sidebar-icon-img" />
  ),
  [DASHBOARD_ROUTE_CONFIG[303].route]: <ChartArea className="sidebar-icon-img" />,
  [DASHBOARD_ROUTE_CONFIG[304].route]: (
    <SquareChartGantt
      className="sidebar-icon-img"
      style={{ color: "#ffffff", stroke: "#ffffff" }}
    />
  ),
  [DASHBOARD_ROUTE_CONFIG[3].route]: (
    <Layers
      className="sidebar-icon-img"
      style={{ color: "#ffffff", stroke: "#ffffff" }}
    />
  ),
  "/account/my-account/account-settings": (
    <img
      src="/Icons/Settings.svg"
      alt="Settings"
      className="sidebar-icon-img"
    />
  ),
  "/account/my-account/notification-preferences": (
    <img
      src="/Icons/Preference.svg"
      alt="Preference"
      className="sidebar-icon-img"
    />
  ),
  "/account/my-account/all-notifications": (
    <img
      src="/Icons/notification.svg"
      alt="Notifications"
      className="sidebar-icon-img"
    />
  ),
};

const Sidebar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { isRTL } = useLanguage();
  // Collapsed-rail tooltips and the mobile drawer sit against the sidebar's
  // outer edge, which swaps sides with the writing direction.
  const tooltipPlacement = isRTL ? "left" : "right";
  const drawerPlacement = isRTL ? "right" : "left";

  const [mode, setMode] = useState<SidebarMode>("collapsed");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [openSubMenus, setOpenSubMenus] = useState<string[]>([]);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [dashboardItems, setDashboardItems] = useState<SidebarItem[]>([]);

  useEffect(() => {
    let mounted = true;

    const loadDashboardItems = async () => {
      const userId = localStorage.getItem("user_id");
      const apiKey = localStorage.getItem("api_key");

      if (!userId || !apiKey) {
        if (mounted) {
          setDashboardItems([]);
        }
        return;
      }

      try {
        const dashboards = await fetchAllDashboardsAPI(
          userId,
          apiKey,
          getApiLangFromI18n(i18n),
        );
        if (!mounted) {
          return;
        }

        const mappedDashboards = buildUserAccessibleDashboardsFromApi(
          dashboards,
        ).map(dashboard => ({
          name: dashboard.dashboardName,
          route: dashboard.route,
        }));

        setDashboardItems(mappedDashboards);
      } catch {
        if (mounted) {
          setDashboardItems([]);
        }
      }
    };

    loadDashboardItems();

    return () => {
      mounted = false;
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  const sections = filterSidebarSections(
    [
      {
        key: "dashboards",
        icon: <img src="/Icons/DashboardIcon.svg" className="sidebar-icon-img" />,
        label: t("sidebar.sections.dashboards"),
        items: dashboardItems,
      },
      {
        key: "dataSources",
        icon: (
          <img
            src="/Icons/SourcesIcon.svg"
            alt="Sources"
            className="sidebar-icon-img"
          />
        ),
        label: t("sidebar.sections.connectedDataSources"),
        route: "/account/connect-sources",
        items: [],
      },
      {
        key: "aiChatbot",
        icon: (
          <img
            src="/Icons/chatbot-logo.png"
            alt="aichatbot"
            className="sidebar-img"
          />
        ),
        label: t("sidebar.sections.askDataTram"),
        route: "/account/ai-chat",
        items: [],
      },
      {
        key: "userManagement",
        icon: (
          <img
            src="/Icons/UserIcon.svg"
            alt="UserManagement"
            className="sidebar-icon-img"
          />
        ),
        label: t("sidebar.sections.userManagement"),
        route: "/account/user-management",
        items: [],
      },
      {
        key: "settings",
        icon: (
          <img
            src="/Icons/AccountIcon.svg"
            alt="MyAccount"
            className="sidebar-icon-img"
          />
        ),
        label: t("sidebar.sections.accountSettings"),
        items: [
          {
            name: t("sidebar.items.myAccount"),
            route: "/account/my-account/account-settings",
          },
          {
            name: t("sidebar.items.notificationPreferences"),
            route: "/account/my-account/notification-preferences",
          },
          {
            name: t("sidebar.items.allNotifications"),
            route: "/account/my-account/all-notifications",
          },
        ],
      },
    ]
  );

  useEffect(() => {
    if (isMobile) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        mode === "open" &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        setMode("collapsed");
        setOpenSections([]);
        setOpenSubMenus([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mode, isMobile]);

  const toggleSection = (key: string) => {
    setOpenSections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleSubMenu = (name: string) => {
    setOpenSubMenus(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [name]
    );
  };

  const resolveItemIcon = (item: SidebarItem) => {
    if (item.route) {
      return staticRouteIconMap[item.route] ?? dashboardIconMap[item.name];
    }
    return dashboardIconMap[item.name];
  };

  const handleLogoClick = () => {
    const firstDashboardItem = sections.find(
      section => section.key === "dashboards"
    )?.items?.[0];
    const firstDashboardRoute = firstDashboardItem?.route ?? DASHBOARD_APP_BASE;
    navigate(firstDashboardRoute);
    if (firstDashboardItem?.name) {
      setActiveSection("dashboards");
      setActiveItem(firstDashboardItem.name);
    }
  };

  const handleHelpSupportClick = () => {
    navigate(HELP_SUPPORT_ROUTE);
    setActiveSection("helpAndSupport");
    setActiveItem(null);
    setOpenSections([]);
    setOpenSubMenus([]);
    if (isMobile) {
      closeDrawer();
    }
  };

  const renderLogo = () => (
    <div className="sidebar-logo">
      <div className="demo-logo-vertical" onClick={handleLogoClick}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={mode === "collapsed" ? "30" : "40"}
          height={mode === "collapsed" ? "30" : "40"}
          viewBox="0 0 35 32"
          fill="none"
        >
          <path
            d="M1 30.5C12.2113 27.974 5.52192 2.0716 17 1.5C28.5918 0.922731 22.2352 27.7056 33.5 30.5"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>
  );

  const effectiveMode: SidebarMode = isMobile ? "open" : mode;
  const closeDrawer = () => {
    setDrawerVisible(false);
    setOpenSections([]);
    setOpenSubMenus([]);
  };

  const renderSidebarContent = () => (
    <div className={`sidebar-inner ${effectiveMode}`}>
      {renderLogo()}

      <div className="sidebar-sections">
        {sections.map(section => (
          <div key={section.key} className="sidebar-section">
            <Tippy
              disabled={effectiveMode === "open"}
              content={section.label}
              placement={tooltipPlacement}
            >
              <div
                className={`section-header ${activeSection === section.key ? "section-active" : ""
                  } ${effectiveMode}`}
                onClick={() => {
                  if (!isMobile && effectiveMode === "collapsed") {
                    setMode("open");
                    setOpenSections([section.key]);
                    return;
                  }

                  if (section.items && section.items.length > 0) {
                    toggleSection(section.key);
                    setActiveSection(null);
                  } else if (section.route) {
                    navigate(section.route);
                    setActiveSection(section.key);
                    setActiveItem(null);
                    if (isMobile) {
                      closeDrawer();
                    }
                  }
                }}
                onMouseEnter={() => {
                  if (!isMobile && effectiveMode === "collapsed") {
                    setMode("open");
                  }
                }}
              >
                <span className="icon">{section.icon}</span>
                {effectiveMode === "open" && (
                  <>
                    <span className="label">{section.label}</span>
                    {section.items && section.items.length > 0 && (
                      <DownOutlined className="section-arrow" />
                    )}
                  </>
                )}
              </div>
            </Tippy>

            {effectiveMode === "open" &&
              openSections.includes(section.key) &&
              section.items?.map(item => {
                const isSubActive = activeItem === item.name;
                return (
                  <div key={item.name}>
                    <div
                      className={`sub-item ${isSubActive ? "sub-active" : ""}`}
                      onClick={() => {
                        if (item.children) {
                          toggleSubMenu(item.name);
                        } else if (item.route) {
                          navigate(item.route);
                          setActiveItem(item.name);
                          setActiveSection(null);
                          if (isMobile) {
                            closeDrawer();
                          }
                        }
                      }}
                    >
                      <span className="item-icon">{resolveItemIcon(item)}</span>
                      <span className="item-label">{item.name}</span>
                      {item.children && (
                        <DownOutlined className="submenu-arrow" />
                      )}
                    </div>

                    {item.children &&
                      openSubMenus.includes(item.name) &&
                      item.children.map(child => {
                        return (
                          <div
                            className={`sub-sub-item ${activeItem === child.name
                                ? "sub-sub-active"
                                : ""
                              }`}
                            onClick={() => {
                              navigate(child.route!);
                              setActiveItem(child.name);
                              setActiveSection(null);
                              if (isMobile) {
                                closeDrawer();
                              }
                            }}
                          >
                            <span className="icon">{resolveItemIcon(child)}</span>
                            <span className="label">{child.name}</span>
                          </div>
                        );
                      })}
                  </div>
                );
              })}
          </div>
        ))}
      </div>

      <Tippy
        disabled={effectiveMode === "open"}
        content={t("sidebar.sections.helpAndSupport")}
        placement={tooltipPlacement}
      >
        <div
          className={`sidebar-footer ${effectiveMode} ${activeSection === "helpAndSupport" ? "section-active" : ""
            }`}
          onClick={handleHelpSupportClick}
        >
          {effectiveMode === "open" && (
            <span className="label">{t("sidebar.sections.helpAndSupport")}</span>
          )}
          <span className="sidebar-footer__icon">
            <CircleHelp size={18} />
          </span>
        </div>
      </Tippy>
    </div>
  );

  return (
    <>
      {isMobile && (
        <button
          className="sidebar-hamburger"
          onClick={() => setDrawerVisible(true)}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
      )}
      {isMobile ? (
        <Drawer
          placement={drawerPlacement}
          closable={false}
          onClose={closeDrawer}
          open={drawerVisible}
          styles={{ body: { padding: 0, backgroundColor: "#2c68ad" } }}
          width={280}
        >
          <div className="sidebar sidebar--drawer open">{renderSidebarContent()}</div>
        </Drawer>
      ) : (
        <Sider
          ref={sidebarRef}
          width={effectiveMode === "collapsed" ? 100 : 280}
          className={`sidebar ${effectiveMode}`}
        >
          {renderSidebarContent()}
        </Sider>
      )}
    </>
  );
};

export default Sidebar;
